import * as _ from 'underscore';

import { Tags } from '../../content/resources/Tags';
import { UserNames } from '../../content/resources/UserNames';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { Groups } from '../../userManagement/groups/Groups';
import { Users } from '../../userManagement/services/resources/Users';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { ScheduleContentType } from '../models/ContentTypes';
import { ISliceable } from '../models/ISliceable';
import { ITypeaheadSource } from '../models/ITypeaheadSource';
import { Module } from '../Module';
import { DataConnectionTypes } from '../resources/DataConnectionTypes';
import { Favorites } from '../resources/Favorites';
import { ProjectNames } from '../resources/ProjectNames';
import { ResourceQuery } from '../resources/ResourceQuery';
import { Schedules } from '../resources/Schedules';
import { SiteNameSansIds } from '../resources/SiteNameSansIds';
import { DataConnectionTypeKeys, IDataConnectionType } from '../ServerApi/types/IDataConnection';
import { IFavorite } from '../ServerApi/types/IFavorite';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { GroupKeys, IGroup } from '../ServerApi/types/IGroup';
import { IProjectName } from '../ServerApi/types/IProject';
import { ISiteNameSansId } from '../ServerApi/types/ISiteNameSansId';
import { ITag } from '../ServerApi/types/ITag';
import { IUserName, UserKeys } from '../ServerApi/types/IUser';
import { NamedItemKeys } from '../ServerApi/types/resourceParts';
import { ServerService } from './ServerService';
import { SliceableFactory } from './SliceableFactory';

export class SearchSuggestionsService {

  public static $inject = [
    'SliceableFactory',
    'ProjectNames',
    'DataConnectionTypes',
    'SiteNamesAcrossAllPods',
    'Favorites',
    'Schedules',
    'ServerService',
    'scheduleFrequencyDescriptionFilter'
  ];

  constructor(
    private SliceableFactory: SliceableFactory,
    private ProjectNames: ProjectNames,
    private DataConnectionTypes: DataConnectionTypes,
    private SiteNamesAcrossAllPods: SiteNameSansIds,
    private Favorites: Favorites,
    private Schedules: Schedules,
    private ServerService: ServerService,
    private ScheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter
  ) { }

  public project(id: string): ng.IPromise<IFieldValue> {
    return this.ProjectNames.getById(id).then((projectName: IProjectName) => {
      return {
        value: projectName ? projectName.id : id,
        label: projectName ? projectName.name : '',
        count: 0
      };
    });
  }

  public sites(matchText: string): ISliceable<ISiteNameSansId> {
    return this.SiteNamesAcrossAllPods.all().withFilters([Filter.search(matchText)]);
  }

  public favorites(matchText: string): ISliceable<IFavorite> {
    return this.Favorites.all().withFilters([Filter.search(matchText)]);
  }

  public users(matchText: string): ResourceQuery<IUserName> {
    return UserNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(UserKeys.displayName)]);
  }

  public usersWithoutGuest(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(Users.instance.getWithoutGuestFilter());
  }

  public usersCanOwnProject(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(Users.instance.getCanOwnProjectFilter());
  }

  public user(id: string): ng.IPromise<IFieldValue> {
    return UserNames.getById(id).then((userName: IUserName) => {
      return {
        value: userName ? userName.id : id,
        label: userName ? userName.displayName : '',
        count: 0
      };
    });
  }

  public groups(matchText: string): ISliceable<IGroup> {
    return Groups.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(GroupKeys.name)]);
  }

  public tags(matchText: string): ISliceable<ITag> {
    return Tags.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
  }

  public dataConnectionType(dataConnectionType: string): ng.IPromise<IFieldValue> {
    return this.DataConnectionTypes.getByDataConnectionType(dataConnectionType).then((connectionType: IDataConnectionType) => {
      return {
        value: connectionType ? connectionType.connectionType : dataConnectionType,
        label: connectionType ? connectionType.connectionTypeDisplayName : '',
        count: 0
      };
    });
  }

  public schedulesSource(scheduledActionType?: string): ITypeaheadSource {
    const searchFilterFn = (schedule: IScheduleInfo, search: string) => {
      if(!search) return true;

      const scheduleName = this.ServerService.displayScheduleDescriptionAsName() ? this.ScheduleFrequencyDescriptionFilter(schedule) : schedule.name;
      return _.all(search.toLowerCase().split(' '), (searchPart) => (scheduleName.toLowerCase().indexOf(searchPart) >= 0));
    };

    let scheduleResourceQuery = this.Schedules.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(Schedules.filterByActionType(scheduledActionType));
    }

    const scheduleListPromise = SliceableHelper.fetchAll(scheduleResourceQuery).then((schedulesList) => schedulesList.items);

    return this.SliceableFactory.searchArray(searchFilterFn, scheduleListPromise);
  }

  public schedulesQuery(scheduledActionType?: string): ResourceQuery<IScheduleInfo> {
    let scheduleResourceQuery = this.Schedules.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(Schedules.filterByActionType(scheduledActionType));
    }
    return scheduleResourceQuery;
  }
}

Module.instance().service('SearchSuggestionsService', SearchSuggestionsService);
